import api from '@/lib/axios';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export type TeacherClassChangeRequestStatus = 'pending' | 'approved' | 'rejected' | 'partial_failure';

export interface TeacherClassChangeRequest {
  id: string;
  teacherId: string;
  teacherName: string | null;
  currentSchoolId: string;
  newSchoolId: string;
  newSectionId: string;
  newClassName: string | null;
  newSectionName: string | null;
  reason: string | null;
  status: TeacherClassChangeRequestStatus;
  requestedBy: string | null;
  requestedByName: string | null;
  remarks: string | null;
  approvedBy: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  rejectedBy: string | null;
  rejectedByName: string | null;
  rejectedAt: string | null;
  partialFailureNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TccSchoolOption {
  id: string;
  name: string;
}

export interface TccSectionOption {
  id: string;
  name: string;
}

export interface TccClassOption {
  id: string;
  name: string;
  sections: TccSectionOption[];
}

export interface TccTeacherOption {
  id: string;
  name: string | null;
  employeeId: string;
  designation: string;
}

export interface CreateTeacherClassChangeRequestPayload {
  teacherId: string;
  newSchoolId: string;
  newSectionId: string;
  reason?: string;
}

export interface UpdateTeacherClassChangeRequestPayload {
  newSchoolId?: string;
  newSectionId?: string;
  reason?: string;
}

/** School tier — create/edit/withdraw the requesting school's own requests. */
export const SchoolTeacherClassChangeRequestsApi = {
  list: async (): Promise<TeacherClassChangeRequest[]> => unwrap(await api.get('/school/teacher-class-change-requests')),

  teachers: async (): Promise<TccTeacherOption[]> => unwrap(await api.get('/school/teacher-class-change-requests/teachers')),

  targetSchools: async (): Promise<TccSchoolOption[]> =>
    unwrap(await api.get('/school/teacher-class-change-requests/target-schools')),

  targetClasses: async (schoolId: string): Promise<TccClassOption[]> =>
    unwrap(await api.get(`/school/teacher-class-change-requests/target-schools/${schoolId}/classes`)),

  create: async (payload: CreateTeacherClassChangeRequestPayload): Promise<TeacherClassChangeRequest> =>
    unwrap(await api.post('/school/teacher-class-change-requests', payload)),

  update: async (id: string, payload: UpdateTeacherClassChangeRequestPayload): Promise<TeacherClassChangeRequest> =>
    unwrap(await api.patch(`/school/teacher-class-change-requests/${id}`, payload)),

  withdraw: async (id: string) => unwrap(await api.delete(`/school/teacher-class-change-requests/${id}`)),
};

export interface ListOrgTeacherClassChangeRequestsParams {
  status?: TeacherClassChangeRequestStatus;
  schoolId?: string;
}

export interface CreateOrgTeacherClassChangeRequestPayload {
  currentSchoolId: string;
  teacherId: string;
  newSchoolId: string;
  newSectionId: string;
  reason?: string;
}

/** Org tier — create (on behalf of any school in scope), global view, and approve/reject, one console page. */
export const OrgTeacherClassChangeRequestsApi = {
  list: async (params?: ListOrgTeacherClassChangeRequestsParams): Promise<TeacherClassChangeRequest[]> =>
    unwrap(await api.get('/org/teacher-class-change-requests', { params })),

  schools: async (): Promise<TccSchoolOption[]> => unwrap(await api.get('/org/teacher-class-change-requests/schools')),

  classesForSchool: async (schoolId: string): Promise<TccClassOption[]> =>
    unwrap(await api.get(`/org/teacher-class-change-requests/schools/${schoolId}/classes`)),

  teachersForSchool: async (schoolId: string): Promise<TccTeacherOption[]> =>
    unwrap(await api.get(`/org/teacher-class-change-requests/schools/${schoolId}/teachers`)),

  create: async (payload: CreateOrgTeacherClassChangeRequestPayload): Promise<TeacherClassChangeRequest> =>
    unwrap(await api.post('/org/teacher-class-change-requests', payload)),

  approve: async (id: string, remarks?: string): Promise<TeacherClassChangeRequest> =>
    unwrap(await api.patch(`/org/teacher-class-change-requests/${id}/approve`, { remarks })),

  reject: async (id: string, remarks?: string): Promise<TeacherClassChangeRequest> =>
    unwrap(await api.patch(`/org/teacher-class-change-requests/${id}/reject`, { remarks })),
};
